/**
 * Render a stored audit value for display, formatting it as a date when it is one.
 *
 * Audit rows hold whatever the field held, so a date arrives in whatever shape it was stored in — ISO,
 * `2026-03-01`, or `Mar 1 2026`. Showing a raw ISO timestamp next to a hand-typed value reads as a bug,
 * so date-looking values are normalised to `MM/DD/YYYY` (plus `HH:mm` when the value carried a time).
 *
 * Everything else is returned untouched. In particular a value must never be *guessed* into a date:
 * `Date.parse` accepts plenty of things that aren't dates, so the regex requires either a month name or
 * an ISO `YYYY-MM-DD` before any reformatting happens.
 *
 * Extracted from the old `AuditLogMapper`, which is otherwise gone now that the trail is composed
 * server-side — this is the one piece of it that carried real behaviour.
 */
export function formatAuditValue<T>(value: T): T | string {
  if (typeof value !== 'string') return value;

  const looksLikeDate = /(\b[A-Z][a-z]{2}\b|\d{4}-\d{2}-\d{2})/.test(value);
  if (!looksLikeDate) return value;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const hasTime = /T|\d{2}:\d{2}/.test(value);

  // A date-ONLY ISO string (`2025-02-03`) is parsed as UTC midnight per spec, so reading it back with
  // local getters shifts it a day earlier anywhere west of UTC — an audit trail would show 02/02/2025
  // for a date recorded as the 3rd. On an I-9 that is not cosmetic: these dates decide whether Section 2
  // met its three-business-day deadline. Month-name and full-timestamp forms parse as local, so those
  // read back with local getters.
  const utc = !hasTime && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());

  const year = utc ? date.getUTCFullYear() : date.getFullYear();
  const month = (utc ? date.getUTCMonth() : date.getMonth()) + 1;
  const day = utc ? date.getUTCDate() : date.getDate();

  let formatted = `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;

  // Only show a time the value actually carried; appending 00:00 to a plain date would invent precision.
  if (hasTime) {
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    formatted += ` ${hh}:${min}`;
  }
  return formatted;
}
